import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { mobileInquiryService } from '../services/mobileInquiryService';
import { useApiError } from '../../shared/hooks/useApiError';
import { MessageDialog } from '../../shared/ui';
import Footer from '../components/Footer';

// CSS-in-JS スタイル
const styles = {
  container: {
    minHeight: '100vh',
    background: 'white',
    position: 'relative',
    paddingBottom: '60px'
  },

    // ヘッダー追加（オブジェクト画面・ユーザー情報画面と同様、IDなし）
    detailHeader: {
      width: '100%',
      height: '40px',
      background: '#444',
      position: 'fixed',
      top: 0,
      zIndex: 1000
    },
    headerLeft: {
      float: 'left'
    },
    showList: {
      width: '32px',
      height: '32px',
      margin: '5px 6px',
      display: 'inline-block',
      backgroundImage: 'url("/core/img/icon/back-arrow-button.png")',
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'contain',
      float: 'left',
      cursor: 'pointer'
    },

  title: {
    margin: '0px 10px',
    padding: '5px 0 5px 20px',
    background: '#262222',
    color: '#FFF',
    fontSize: '12pt',
    fontWeight: 'bold'
  },
  info: {
    padding: '3px 10px',
    margin: '4px 10px',
    fontSize: '9pt',
    lineHeight: 1.4,
    color: '#333'
  },
  detail: {
    margin: '6px 10px',
    padding: '8px',
    border: '1px solid #BBB',
    borderRadius: '4px',
    background: '#ffffff'
  },
  item: {
    margin: '8px 0 4px 0',
    fontSize: '11pt',
    fontWeight: 'bold',
    color: '#333'
  },
  inputField: {
    width: '98%',
    margin: '2px 0',
    padding: '8px',
    border: '1px solid',
    borderColor: '#ddd',
    borderRadius: '4px',
    fontSize: '15px',
    background: '#ffffff'
  },
  inputFieldFocus: {
    outline: 'none',
    borderColor: '#2B2222',
    background: 'white'
  },
  inputFieldError: {
    borderColor: '#ff6b6b',
    background: '#fff5f5'
  },
  textarea: {
    width: '98%',
    height: '140px',
    margin: '2px 0',
    padding: '8px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '15px',
    background: '#ffffff',
    resize: 'vertical'
  },
  submitButton: {
    width: '90%',
    display: 'block',
    margin: '12px auto',
    padding: '6px 0',
    background: '#F64349',
    color: '#ffffff',
    border: 'none',
    borderRadius: '30px',
    fontSize: '14pt',
    fontWeight: '900',
    letterSpacing: '16px',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease'
  },
  buttonHover: {
    background: '#e63946'
  },
  buttonDisabled: {
    background: '#cccccc',
    cursor: 'not-allowed',
    opacity: 0.7
  },
  backToLoginButton: {
    width: '90%',
    display: 'block',
    margin: '15px auto',
    padding: '10px 0',
    background: '#262222',
    color: '#ffffff',
    border: 'none',
    borderRadius: '24px',
    fontSize: '15px',
    letterSpacing: '6px',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease'
  },
  backToLoginButtonHover: {
    background: '#4a4a4a'
  },
  formArea: {
    padding: '0 20px',
    maxWidth: '1024px',
    margin: '0 auto',
  },
  buttonContainer: {
    margin: '15px 0'
  }
};

const AppInquiry = () => {
  const navigate = useNavigate();
  
  // APIエラーハンドリングフック
  const { 
    showErrorDialog,
    errorMessage,
    handleApiError,
    closeErrorDialog
  } = useApiError(() => setIsLoading(false))
  
  // フォームデータ
  const [name, setName] = useState(''); // 名前
  const [mail, setMail] = useState(''); // メールアドレス
  const [subject, setSubject] = useState(''); // 件名
  const [content, setContent] = useState(''); // 内容
  const [isLoading, setIsLoading] = useState(false); // ローディング状態
  const [error, setError] = useState(''); // エラーメッセージ
  const [loginFlg, setLoginFlg] = useState(false); // ログイン状態フラグ
  const [showSuccessDialog, setShowSuccessDialog] = useState(false); // 成功ダイアログ表示フラグ
  
  // フィールドエラー状態
  const [fieldErrors, setFieldErrors] = useState({
    name: false, // 名前エラー
    mail: false, // メールアドレスエラー
    subject: false, // 件名エラー
    content: false // 内容エラー
  });

  // コンポーネント初期化
  useEffect(() => {
    const fetchUserInfo = async () => {
      setIsLoading(true);
      try {
        const result = await mobileInquiryService.getUserInfo();

        if(result.returnCode !== '200'){
          MessageDialog.show('エラー', 'ユーザー情報の取得に失敗しました。', null, 'error');
        }
        
        if (result.userName) {
          setName(result.userName);
        }
        if (result.mailAddress) {
          setMail(result.mailAddress);
        }
        // ログイン状態の判定
        if (result.userName && result.mailAddress && result.userName.trim() !== '' && result.mailAddress.trim() !== '') {
          setLoginFlg(true);
        } else {
          setLoginFlg(false);
        }
      } catch (error) {
        // エラーが発生した場合は未ログイン状態とする
        setLoginFlg(false);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // バリデーション関数群
  const validateMailAddress = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    const newFieldErrors = {
      name: !name.trim(),
      mail: !mail.trim(),
      subject: !subject.trim(),
      content: !content.trim()
    };
    
    setFieldErrors(newFieldErrors);
    
    if (newFieldErrors.name) {
      setError('名前を入力してください。');
      return false;
    }
    if (newFieldErrors.mail) {
      setError('メールアドレスを入力してください。');
      return false;
    }
    if (!validateMailAddress(mail)) {
      setError('正しいメールアドレスを入力してください。');
      setFieldErrors(prev => ({ ...prev, mail: true }));
      return false;
    }
    if (newFieldErrors.subject) {
      setError('件名を入力してください。');
      return false;
    }
    if (newFieldErrors.content) {
      setError('内容を入力してください。');
      return false;
    }
    return true;
  };

  // お問い合わせ送信処理
  const handleSubmit = async () => {
    if (!validateForm()) {
      // エラー時に最初のエラーフィールドにフォーカスを移動
      setTimeout(() => {
        if (fieldErrors.name) {
          document.querySelector('input[aria-label="名前"]')?.focus();
        } else if (fieldErrors.mail) {
          document.querySelector('input[aria-label="メールアドレス"]')?.focus();
        } else if (fieldErrors.subject) {
          document.querySelector('input[aria-label="件名"]')?.focus();
        } else if (fieldErrors.content) {
          document.querySelector('textarea[aria-label="内容"]')?.focus();
        }
      }, 0);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const result = await mobileInquiryService.sendInquiry({
        name,
        mail,
        subject,
        content
      });
      
      // ビジネスロジックエラーのチェック
      if (result.returnCode !== '200') {
        MessageDialog.show('エラー', 'お問い合わせの送信に失敗しました。', null, 'error');
        setIsLoading(false);
        return;
      }

      // 成功時の処理
      setShowSuccessDialog(true);
    } catch (error) {
      handleApiError(new Error('お問い合わせの送信に失敗しました。'));
    } finally {
      setIsLoading(false);
    }
  };

  // フォーム操作関数群
  const handleClear = () => {
    // ログイン済みの場合は名前とメールアドレスを保持
    if (loginFlg) {
      setSubject('');
      setContent('');
    } else {
      // 未ログインの場合は全てクリア
      setName('');
      setMail('');
      setSubject('');
      setContent('');
    }
    setError('');
    setFieldErrors({
      name: false,
      mail: false,
      subject: false,
      content: false
    });
  };

  // ヘッダー戻るボタン
  const handleBack = () => {
    if (loginFlg) {
      navigate('/appConfig');
    } else {
      navigate('/appLogin');
    }
  };

  // ナビゲーション処理
  const handleBackToLogin = () => {
    navigate('/appLogin');
  };

  // ダイアログ処理
  const handleSuccessDialogClose = () => {
    setShowSuccessDialog(false);
    handleClear();
  };

  // キーボードイベントハンドラー
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !isLoading) {
      e.preventDefault();
      handleSubmit();
    }
  };

  // ローディング中の表示
  if (isLoading) {
    return (
      <div style={styles.container}>
               {loginFlg && (
        <>
          <div style={styles.detailHeader}>
            <div style={styles.headerLeft}>
              <div
                style={styles.showList}
                onClick={handleBack}
                data-testid="back-button"
                role="button"
                aria-label="戻る"
              />
            </div>
          </div>
        </>
        )}
        <div style={{ ...styles.formArea, ...(loginFlg ? { marginTop: '60px' } : {}) }}>
          <div style={styles.title}>
            確認中
          </div>
          <div style={styles.info}>
            ユーザー情報を確認しています...
          </div>
        </div>
      </div>
    );
  }



  // フォーム入力画面
  return (
    <div style={styles.container} onKeyDown={handleKeyDown}>
              {loginFlg && (
        <>
          <div style={styles.detailHeader}>
            <div style={styles.headerLeft}>
              <div
                style={styles.showList}
                onClick={handleBack}
                data-testid="back-button"
                role="button"
                aria-label="戻る"
              />
            </div>
          </div>
        </>
        )}
        <div style={{ ...styles.formArea, ...(loginFlg ? { marginTop: '60px' } : {}) }}>
        <div style={styles.title}>
          お問い合わせ
        </div>
        <div style={styles.info}>
          下記のフォームより問い合わせをお願いします。<br />
          内容によっては回答にお時間をいただくこともございます。
        </div>
        <div style={styles.detail}>
          <div style={styles.item}>名前 *</div>
                     <input
             type="text"
             value={name}
             onChange={(e) => setName(e.target.value)}
             style={{
               ...styles.inputField,
               ...(fieldErrors.name ? styles.inputFieldError : {})
             }}
             onFocus={(e) => {
               e.target.style.outline = 'none';
               e.target.style.borderColor = '#2B2222';
               e.target.style.background = fieldErrors.name ? '#fff5f5' : 'white';
             }}
             onBlur={(e) => {
               e.target.style.borderColor = fieldErrors.name ? '#ff6b6b' : '#ddd';
               e.target.style.background = fieldErrors.name ? '#fff5f5' : '#ffffff';
             }}
             aria-label="名前"
           />

          <div style={styles.item}>メールアドレス *</div>
                     <input
             type="email"
             value={mail}
             onChange={(e) => setMail(e.target.value)}
             style={{
               ...styles.inputField,
               ...(fieldErrors.mail ? styles.inputFieldError : {})
             }}
             onFocus={(e) => {
               e.target.style.outline = 'none';
               e.target.style.borderColor = '#2B2222';
               e.target.style.background = fieldErrors.mail ? '#fff5f5' : 'white';
             }}
             onBlur={(e) => {
               e.target.style.borderColor = fieldErrors.mail ? '#ff6b6b' : '#ddd';
               e.target.style.background = fieldErrors.mail ? '#fff5f5' : '#ffffff';
             }}
             aria-label="メールアドレス"
           />

          <div style={styles.item}>件名 *</div>
                     <input
             type="text"
             value={subject}
             onChange={(e) => setSubject(e.target.value)}
             style={{
               ...styles.inputField,
               ...(fieldErrors.subject ? styles.inputFieldError : {})
             }}
             onFocus={(e) => {
               e.target.style.outline = 'none';
               e.target.style.borderColor = '#2B2222';
               e.target.style.background = fieldErrors.subject ? '#fff5f5' : 'white';
             }}
             onBlur={(e) => {
               e.target.style.borderColor = fieldErrors.subject ? '#ff6b6b' : '#ddd';
               e.target.style.background = fieldErrors.subject ? '#fff5f5' : '#ffffff';
             }}
             aria-label="件名"
           />

          <div style={styles.item}>内容 *</div>
                     <textarea
             value={content}
             onChange={(e) => setContent(e.target.value)}
             style={{
               ...styles.textarea,
               ...(fieldErrors.content ? styles.inputFieldError : {})
             }}
             onFocus={(e) => {
               e.target.style.outline = 'none';
               e.target.style.borderColor = '#2B2222';
               e.target.style.background = fieldErrors.content ? '#fff5f5' : 'white';
             }}
             onBlur={(e) => {
               e.target.style.borderColor = fieldErrors.content ? '#ff6b6b' : '#ddd';
               e.target.style.background = fieldErrors.content ? '#fff5f5' : '#ffffff';
             }}
             aria-label="内容"
           />
        </div>

        {error && (
          <div style={{
            color: '#ff6b6b',
            textAlign: 'center',
            margin: '8px 0',
            fontSize: '13px'
          }}>
            {error}
          </div>
        )}

        <div style={styles.buttonContainer}>
          <button 
            onClick={handleSubmit}
            disabled={isLoading}
            style={{
              ...styles.submitButton,
              ...(isLoading ? styles.buttonDisabled : {})
            }}
            onMouseEnter={(e) => {
              if (!isLoading) {
                e.target.style.background = '#e63946';
              }
            }}
            onMouseLeave={(e) => {
              if (!isLoading) {
                e.target.style.background = '#F64349';
              }
            }}
          >
            {isLoading ? '送信中...' : '送信'}
          </button>

          {!loginFlg && (
            <button 
              onClick={handleBackToLogin}
              style={styles.backToLoginButton}
              onMouseEnter={(e) => {
                e.target.style.background = '#4a4a4a';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = '#262222';
              }}
            >
              ログインへ戻る
            </button>
          )}
        </div>
      </div>

      {/* ログイン済みの場合のみフッターを表示 */}
      {loginFlg && <Footer />}

      {/* Error Dialog */}
      <MessageDialog
        isOpen={showErrorDialog}
        onClose={closeErrorDialog}
        title="メッセージ"
        message={errorMessage}
        type="error"
      />

      {/* Success Dialog */}
      <MessageDialog
        isOpen={showSuccessDialog}
        onClose={handleSuccessDialogClose}
        title="メッセージ"
        message="メール送信が完了しました。"
        type="success"
      />
    </div>
  );
};

export default AppInquiry;

